import type { DevicePlatform } from "@speakai/shared";
import type { ApiHttpClient, RequestOptions } from "../interfaces/api-http-client";
import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  SSOLoginRequest,
  SSOType,
  VerifyOTPRequest,
  RefreshTokenRequest,
  SessionResponse,
  DesktopHandoffRequest,
  DesktopHandoffResponse,
  ChangePasswordRequest,
  UserProfile,
  MobileAuthRequest,
  MobileSsoRequest,
} from "./types";

/** Auth API calls, shared across every platform. */
export function createAuthApi(http: ApiHttpClient) {
  return {
    login(data: LoginRequest) {
      return http.post<LoginResponse>("/auth/login", data);
    },

    register(data: RegisterRequest) {
      return http.post<RegisterResponse>("/auth/register", data);
    },

    ssoLogin(type: SSOType, data: SSOLoginRequest) {
      return http.post<LoginResponse>(`/auth/sso/${type}`, data);
    },

    forgotPassword(email: string) {
      return http.post(
        `/auth/forgotPassword?email=${encodeURIComponent(email)}`,
        null,
      );
    },

    verifyOTP(data: VerifyOTPRequest) {
      return http.post<LoginResponse>("/auth/otp/verify", data);
    },

    resendOTP(email: string) {
      return http.post("/auth/otp/resend", { email });
    },

    verifyEmail(token: string) {
      return http.get(`/auth/verifyToken?token=${encodeURIComponent(token)}`);
    },

    refreshToken(data: RefreshTokenRequest, headers?: Record<string, string>) {
      return http.post<LoginResponse>("/auth/refreshToken", data, headers);
    },

    /**
     * PUT /auth/logout. Web relies on the httpOnly session cookie and needs
     * no params. Mobile has no cookie jar — it must pass its own bearer
     * token as a header, plus a body identifying which device/refresh
     * token to invalidate server-side.
     */
    logout(params?: { refreshToken?: string; deviceId?: string }, headers?: Record<string, string>) {
      return http.put("/auth/logout", params ?? null, headers);
    },

    getSession() {
      return http.get<SessionResponse>("/auth/session");
    },

    changePassword(data: ChangePasswordRequest) {
      return http.post("/auth/updatePassword", data);
    },

    getUserByToken() {
      return http.get<UserProfile>("/user/profile");
    },

    resendInviteEmail(email: string) {
      return http.post("/auth/resendInvite", { email });
    },

    getTeamInvite(token: string) {
      return http.get(`/invite/link/${token}`);
    },

    claimTeamInvite(token: string, email: string, source?: string) {
      return http.post(`/invite/link/${token}/claim`, { email, source });
    },

    getIntercomUserHash(email: string) {
      return http.get<{ intercomUserHash?: string }>(
        `/auth/userHash?email=${encodeURIComponent(email)}`,
      );
    },

    /** GET /auth/intercomJwt?platform=ios|android|desktop[&app=&appVersion=] — Messenger Security. Requires the caller's session token as a header (auth'd endpoint, no cookie jar here); `app` and `appVersion` go into the signed claims. */
    getIntercomJwt(
      platform: DevicePlatform.IOS | DevicePlatform.ANDROID | DevicePlatform.DESKTOP,
      headers?: Record<string, string>,
      client?: { app: string; appVersion: string },
      options?: RequestOptions,
    ) {
      const clientQuery = client
        ? `&app=${encodeURIComponent(client.app)}&appVersion=${encodeURIComponent(client.appVersion)}`
        : "";
      return http.get<{ intercomJwt?: string }>(`/auth/intercomJwt?platform=${platform}${clientQuery}`, headers, options);
    },

    // -- 2FA -----------------------------------------------------------

    generate2FA() {
      return http.post("/auth/2fa/generate", {});
    },

    enable2FA(data: { token: string }) {
      return http.post("/auth/2fa/enable", data);
    },

    disable2FA() {
      return http.put("/auth/2fa/disable", {});
    },

    get2FADevices() {
      return http.get("/auth/2fa/devices");
    },

    revoke2FADevice(data: { deviceIds: string[] }) {
      return http.post("/auth/2fa/devices/revoke", data);
    },

    verify2FA(data: {
      token: string;
      /** Canonical field — set to true if the user opted to trust this device for 30 days.
       *  Server `verifyTwoFactor` ORs this with the legacy `isTrusted` flag from Angular;
       *  callers use `trustDevice` only. Do not add `isTrusted` here. */
      trustDevice?: boolean;
      deviceId: string;
      deviceType?: DevicePlatform.IOS | DevicePlatform.ANDROID;
      email?: string;
    }) {
      // The pre-2FA token is sent automatically via the httpOnly `spk_pre_auth`
      // cookie (set by the server on login when 2FA is required). No need to
      // send it as an explicit header — reduces token exposure surface.
      return http.post<LoginResponse>("/auth/2fa/verify", data);
    },

    // -- Lead / Email verification -------------------------------------

    registerLeadUser(data: {
      email: string;
      firstName: string;
      lastName: string;
      password: string;
      token?: string;
      requestId?: string;
      source?: string;
    }) {
      return http.post<LoginResponse>("/auth/registerLeadUser", data);
    },

    verifyToken(token: string) {
      return http.get(`/auth/verifyToken?token=${encodeURIComponent(token)}`);
    },

    /**
     * POST /auth/updateUser?token=...
     *
     * Team invitee sets name + password using an invite token. Server `verifyUserBody`
     * Joi schema (speak-server validations/auth.ts:40) requires firstName, lastName, and
     * password — partial submissions return 400.
     */
    updateUserByToken(
      token: string,
      data: { firstName: string; lastName: string; password: string },
    ) {
      return http.post(
        `/auth/updateUser?token=${encodeURIComponent(token)}`,
        data,
      );
    },

    // -- Invite --------------------------------------------------------

    getInviteMeta(companySlug: string, token: string) {
      return http.get(
        `/invite/link/${token}?companySlug=${encodeURIComponent(companySlug)}`,
      );
    },

    /**
     * POST /invite/link/:token/claim
     *
     * Body: { email: string; source?: string }
     */
    claimInvite(data: {
      token?: string;
      inviteToken?: string;
      email: string;
      source?: string;
    }) {
      const resolvedToken = data.token || data.inviteToken || "";
      const body: { email: string; source?: string } = { email: data.email };
      if (data.source !== undefined) body.source = data.source;
      return http.post(`/invite/link/${resolvedToken}/claim`, body);
    },

    /**
     * POST /auth/otp/verify
     *
     * Body: { email: string; code: string; requestId?: string }
     */
    verifyInviteOtp(data: { email: string; code?: string; requestId?: string }) {
      return http.post<LoginResponse>("/auth/otp/verify", data);
    },

    /** POST /auth/desktop/handoff: mints a one-time code the desktop app exchanges with its PKCE verifier. */
    createDesktopHandoffCode(data: DesktopHandoffRequest) {
      return http.post<DesktopHandoffResponse>("/auth/desktop/handoff", data);
    },

    // -- Native mobile sign-in ------------------------------------------

    /**
     * POST /auth/mobileAuth — native Google/Apple sign-in. The client
     * completes the provider's own native sign-in SDK flow, then posts the
     * resulting profile + provider tokens here; the server creates/links
     * the account and returns a session the same way login/register do.
     * No web equivalent — web's SSO goes through the popup-based
     * `ssoLogin` above instead.
     */
    mobileAuth(data: MobileAuthRequest) {
      return http.post<LoginResponse>("/auth/mobileAuth", data);
    },

    /**
     * POST /auth/mobile/sso — native Google/Apple sign-in where the server
     * verifies the provider's ID token itself and reads the account's email
     * from it, instead of trusting a profile the client sends.
     */
    mobileSso(data: MobileSsoRequest) {
      return http.post<LoginResponse>("/auth/mobile/sso", data);
    },

  };
}
